import { useEffect, useRef, type FC } from "react";
import type { DotGridColorTheme } from "@/components/backgrounds/dot-grid-types";
import { PATTERNS, type DotGridAnimationMode } from "@/components/backgrounds/dot-grid-patterns";

/**
 * Animated dot grid for onboarding steps.
 *
 * Renders a Canvas 2D dot grid that continuously runs a pattern animation
 * (agents, orchestration, or governance). When `animationMode` changes,
 * the old and new patterns crossfade over 600ms with easeInOutCubic easing.
 * Color themes also crossfade in sync.
 */

const DOT_RADIUS = 2.25;
const DOT_SPACING = 20;
const GRID_OPACITY = 0.7;
const CROSSFADE_MS = 600;
const DIM = 0.08;

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerpRgb(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

type OnboardingDotGridCanvasProps = {
    animationMode: DotGridAnimationMode;
    colorTheme: DotGridColorTheme;
};

export const OnboardingDotGridCanvas: FC<OnboardingDotGridCanvasProps> = ({ animationMode, colorTheme }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Refs to communicate prop changes into the animation loop without re-mounting
    const animationModeRef = useRef(animationMode);
    const colorThemeRef = useRef(colorTheme);

    // Track previous values to detect changes and trigger crossfade
    const prevModeRef = useRef(animationMode);
    const prevColorThemeRef = useRef(colorTheme);

    // Crossfade state lives in a ref so the animation loop can read it without re-renders
    const crossfadeRef = useRef<{
        phase: "idle" | "crossfade";
        startTime: number;
        progress: number;
        fromMode: DotGridAnimationMode;
        fromTimeBase: number;
        fromColors: DotGridColorTheme;
        toMode: DotGridAnimationMode | null;
        toTimeBase: number;
        toColors: DotGridColorTheme | null;
    }>({
        phase: "idle",
        startTime: 0,
        progress: 1,
        fromMode: animationMode,
        fromTimeBase: 0,
        fromColors: colorTheme,
        toMode: null,
        toTimeBase: 0,
        toColors: null,
    });

    // Update refs when props change
    animationModeRef.current = animationMode;
    colorThemeRef.current = colorTheme;

    // Detect mode/theme changes and initiate crossfade
    useEffect(() => {
        if (animationMode === prevModeRef.current && colorTheme === prevColorThemeRef.current) {
            return;
        }

        const cf = crossfadeRef.current;
        const now = performance.now();
        const elapsed = (now - startTimeRef.current) / 1000;

        // If already crossfading, snap the "from" to wherever the blend currently is
        if (cf.phase === "crossfade" && cf.toMode) {
            cf.fromMode = cf.toMode;
            cf.fromTimeBase = cf.toTimeBase;
            cf.fromColors = cf.toColors ?? colorTheme;
        }

        cf.phase = "crossfade";
        cf.toMode = animationMode;
        cf.toTimeBase = elapsed;
        cf.toColors = colorTheme;
        cf.startTime = now;
        cf.progress = 0;

        prevModeRef.current = animationMode;
        prevColorThemeRef.current = colorTheme;
    }, [animationMode, colorTheme]);

    // Stable ref for the animation start time
    const startTimeRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let animFrameId: number;
        let gridRows = 0;
        let gridCols = 0;
        let dots: { x: number; y: number; row: number; col: number }[] = [];
        let w = 0;
        let h = 0;

        startTimeRef.current = performance.now();

        function sizeCanvas() {
            const dpr = window.devicePixelRatio || 1;
            const rect = container!.getBoundingClientRect();
            w = rect.width;
            h = rect.height;

            canvas!.width = w * dpr;
            canvas!.height = h * dpr;
            canvas!.style.width = `${w}px`;
            canvas!.style.height = `${h}px`;
            ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

            gridCols = Math.ceil(w / DOT_SPACING) + 1;
            gridRows = Math.ceil(h / DOT_SPACING) + 1;
            const gridW = (gridCols - 1) * DOT_SPACING;
            const gridH = (gridRows - 1) * DOT_SPACING;
            const offsetX = (w - gridW) / 2;
            const offsetY = (h - gridH) / 2;

            dots = [];
            for (let row = 0; row < gridRows; row++) {
                for (let col = 0; col < gridCols; col++) {
                    dots.push({
                        x: offsetX + col * DOT_SPACING,
                        y: offsetY + row * DOT_SPACING,
                        row,
                        col,
                    });
                }
            }
        }

        function renderFrame() {
            const now = performance.now();
            const elapsed = (now - startTimeRef.current) / 1000;

            // Advance crossfade
            const cf = crossfadeRef.current;
            if (cf.phase === "crossfade") {
                const te = now - cf.startTime;
                if (te >= CROSSFADE_MS) {
                    cf.phase = "idle";
                    cf.progress = 1;
                    cf.fromMode = cf.toMode!;
                    cf.fromTimeBase = cf.toTimeBase;
                    cf.fromColors = cf.toColors!;
                    cf.toMode = null;
                    cf.toColors = null;
                } else {
                    cf.progress = easeInOutCubic(te / CROSSFADE_MS);
                }
            }

            // Compute brightness values
            const n = gridRows * gridCols;
            let colors: DotGridColorTheme;
            const brightness = new Float32Array(n);

            if (cf.phase === "crossfade" && cf.toMode && cf.toColors) {
                const p = cf.progress;
                const fromPattern = PATTERNS[cf.fromMode];
                const toPattern = PATTERNS[cf.toMode];
                const fromValues = fromPattern(elapsed - cf.fromTimeBase, gridRows, gridCols);
                const toValues = toPattern(elapsed - cf.toTimeBase, gridRows, gridCols);

                for (let i = 0; i < n; i++) {
                    const fv = fromValues[i] ?? DIM;
                    const tv = toValues[i] ?? DIM;
                    brightness[i] = DIM + (fv - DIM) * (1 - p) + (tv - DIM) * p;
                }

                // Crossfade colors too
                colors = {
                    base: lerpRgb(cf.fromColors.base, cf.toColors.base, p),
                    bright: lerpRgb(cf.fromColors.bright, cf.toColors.bright, p),
                };
            } else {
                const pattern = PATTERNS[cf.fromMode];
                const values = pattern(elapsed - cf.fromTimeBase, gridRows, gridCols);
                for (let i = 0; i < n; i++) {
                    brightness[i] = values[i] ?? DIM;
                }
                colors = cf.fromColors;
            }

            // Draw
            ctx!.clearRect(0, 0, w, h);
            for (let i = 0; i < dots.length; i++) {
                const dot = dots[i];
                const b = brightness[i];
                const r = colors.base[0] + (colors.bright[0] - colors.base[0]) * b;
                const g = colors.base[1] + (colors.bright[1] - colors.base[1]) * b;
                const bl = colors.base[2] + (colors.bright[2] - colors.base[2]) * b;
                ctx!.beginPath();
                ctx!.arc(dot.x, dot.y, DOT_RADIUS, 0, Math.PI * 2);
                ctx!.fillStyle = `rgb(${r}, ${g}, ${bl})`;
                ctx!.fill();
            }

            animFrameId = requestAnimationFrame(renderFrame);
        }

        sizeCanvas();
        animFrameId = requestAnimationFrame(renderFrame);

        const ro = new ResizeObserver(() => {
            sizeCanvas();
        });
        ro.observe(container);

        // React to dark/light mode changes
        const mo = new MutationObserver(() => {
            // Colors are driven by props (colorTheme), so dark mode changes
            // are handled by the parent re-rendering with new theme values.
            // No action needed here.
        });
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

        return () => {
            cancelAnimationFrame(animFrameId);
            ro.disconnect();
            mo.disconnect();
        };
    }, []); // Single mount — all state changes flow through refs

    return (
        <>
            <style>{`@keyframes dot-grid-fade-in { from { opacity: 0 } to { opacity: ${GRID_OPACITY} } }`}</style>
            <div
                ref={containerRef}
                className="pointer-events-none absolute inset-0 h-full w-full select-none"
                style={{
                    maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                    WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                }}
            >
                <div
                    className="h-full w-full"
                    style={{
                        maskImage: "radial-gradient(100% 100%, transparent 0%, black 50%)",
                        WebkitMaskImage: "radial-gradient(100% 100%, transparent 0%, black 50%)",
                        animation: "dot-grid-fade-in 1.5s ease-out forwards",
                    }}
                >
                    <canvas ref={canvasRef} className="block h-full w-full" />
                </div>
            </div>
        </>
    );
};
